import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useInterval } from "@/lib/useInterval";
import { Users, Activity, ListOrdered, IndianRupee, TrendingDown, TrendingUp, Clock } from "lucide-react";
import SystemHealth from "@/components/SystemHealth";

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);

  useInterval(() => {
    api.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data.items || [])).catch(() => {});
    api.get("/admin/market-status").then((r) => setMarketStatus(r.data)).catch(() => {});
  }, 10000);

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <MarketStatusBar status={marketStatus} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile icon={Users} title="Clients" value={summary?.clients_count ?? "—"} />
        <Tile icon={IndianRupee} title="Total Balance" value={fmtINR(summary?.total_balance)} />
        <Tile icon={Activity} title="Open Positions" value={summary?.total_open_positions ?? "—"} />
        <Tile icon={summary?.total_unrealised_mtm >= 0 ? TrendingUp : TrendingDown}
              title="Unrealised MTM (sum)"
              value={fmtINR(summary?.total_unrealised_mtm)}
              colored={summary?.total_unrealised_mtm} />
        <Tile icon={ListOrdered} title="Pending Orders" value={summary?.pending_orders ?? "—"} />
      </div>

      <SystemHealth />

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex justify-between">
          <div>
            <h2 className="text-lg font-semibold">All Clients</h2>
            <p className="text-xs text-muted-foreground">Live snapshot of {clients.length} clients</p>
          </div>
          <Link to="/admin/clients" className="text-xs font-semibold text-primary hover:underline">Manage →</Link>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <Th>Client ID</Th><Th>Name</Th>
                <Th right>Balance</Th><Th right>Margin Used</Th>
                <Th right>MTM</Th><Th right>Open Pos.</Th>
                <Th>Trading</Th><Th>SL/Tgt</Th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">No clients yet. Go to Clients → Add new.</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-border hover:bg-secondary/40" data-testid={`admin-client-row-${c.client_id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/admin/clients/${c.client_id}`} className="font-mono text-xs font-semibold text-primary hover:underline">{c.client_id}</Link>
                  </td>
                  <td className="px-3 py-2 text-sm">{c.name}</td>
                  <td className="px-3 py-2 text-right num text-xs">{fmtINR(c.balance)}</td>
                  <td className="px-3 py-2 text-right num text-xs">{fmtINR(c.margin_used)}</td>
                  <td className={`px-3 py-2 text-right num text-xs font-semibold ${c.unrealised_mtm > 0 ? "text-profit" : c.unrealised_mtm < 0 ? "text-loss" : ""}`}>{fmtINR(c.unrealised_mtm)}</td>
                  <td className="px-3 py-2 text-right num text-xs">{c.open_positions || 0}</td>
                  <td className="px-3 py-2"><Badge ok={c.trading_enabled} /></td>
                  <td className="px-3 py-2"><Badge ok={c.target_sl_enabled} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 font-semibold ${right ? "text-right" : "text-left"}`}>{children}</th>
);

function Tile({ icon: Icon, title, value, colored }) {
  let cls = "text-xl font-bold mt-1 num";
  if (colored !== undefined) cls += colored > 0 ? " text-profit" : colored < 0 ? " text-loss" : "";
  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {title}
      </div>
      <div className={cls}>{value}</div>
    </div>
  );
}

function Badge({ ok }) {
  return ok ? (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">ON</span>
  ) : (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-destructive font-semibold">OFF</span>
  );
}

/**
 * Phase D follow-up (2026-05-30): admin-only at-a-glance market status row.
 * Powered by GET /api/admin/market-status which resolves Phase D's market_calendar
 * + seasonal baseline to compute live state, today_hours, label and next_open.
 */
function MarketStatusBar({ status }) {
  if (!status) {
    return (
      <div className="bg-white border border-border rounded-xl p-3 grid grid-cols-2 gap-3 animate-pulse"
           data-testid="market-status-loading">
        <div className="h-12 bg-secondary/40 rounded" />
        <div className="h-12 bg-secondary/40 rounded" />
      </div>
    );
  }
  const ex = status.exchanges || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="market-status-bar">
      <ExchangeStatusCard name="NSEFO" info={ex.NSEFO} />
      <ExchangeStatusCard name="MCX" info={ex.MCX} />
    </div>
  );
}

function ExchangeStatusCard({ name, info }) {
  if (!info) return null;
  const isOpen = info.status === "OPEN";
  const isPreOpen = info.status === "PRE_OPEN";
  const colorBg = isOpen ? "bg-emerald-50 border-emerald-300"
                  : isPreOpen ? "bg-amber-50 border-amber-300"
                  : "bg-rose-50 border-rose-200";
  const dotCls = isOpen ? "bg-emerald-500 animate-pulse"
                : isPreOpen ? "bg-amber-500"
                : "bg-rose-400";
  const statusLabel = isOpen ? "OPEN"
                    : isPreOpen ? "PRE-OPEN"
                    : info.status === "CLOSED" ? "CLOSED"
                    : "CLOSED TODAY";
  return (
    <div className={`border rounded-xl p-3 ${colorBg}`}
         data-testid={`market-status-${name.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dotCls}`} />
          <span className="font-bold text-sm uppercase tracking-wide">{name}</span>
          <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-white/60"
                data-testid={`status-${name.toLowerCase()}`}>
            {statusLabel}
          </span>
          {info.label && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/70 font-semibold truncate"
                  title={info.label}>
              {info.label}
            </span>
          )}
        </div>
        <div className="text-[11px] font-semibold text-right shrink-0 flex items-center gap-1">
          <Clock className="h-3 w-3 opacity-60" />
          {isOpen && info.closes_in_minutes != null && (
            <span data-testid={`closes-in-${name.toLowerCase()}`}>
              Closes in {fmtDuration(info.closes_in_minutes)}
              {info.today_hours && <span className="opacity-60 ml-1">· {info.today_hours[1]}</span>}
            </span>
          )}
          {isPreOpen && info.opens_in_minutes != null && (
            <span data-testid={`opens-in-${name.toLowerCase()}`}>
              Opens in {fmtDuration(info.opens_in_minutes)}
              {info.today_hours && <span className="opacity-60 ml-1">· {info.today_hours[0]}</span>}
            </span>
          )}
          {!isOpen && !isPreOpen && info.next_open_human && (
            <span data-testid={`next-open-${name.toLowerCase()}`}>Next: {info.next_open_human}</span>
          )}
        </div>
      </div>
      {info.today_hours && !isOpen && !isPreOpen && (
        <div className="text-[10px] text-muted-foreground mt-1">
          Today's session: <span className="font-mono">{info.today_hours[0]} – {info.today_hours[1]}</span>
        </div>
      )}
    </div>
  );
}

function fmtDuration(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
